"use client";

import { useEffect, useRef, useState } from "react";
import { fetchAndCacheAvailableEventPrice } from "@/utils/fetch-available-event-price";
import {
  getCachedEventStartPrice,
  hasPositiveStartPrice,
  parseStartPrice,
} from "@/utils/event-start-price-cache";

function resolveInitialDisplayPrice(
  data: { id?: number | string; price?: number | string } | undefined,
): number {
  if (hasPositiveStartPrice(data?.price)) {
    return parseStartPrice(data?.price)!;
  }
  if (data?.id != null) {
    const cached = getCachedEventStartPrice(data.id);
    if (cached != null) return cached;
  }
  return 0;
}

export function useEventCardPrice(
  data: { id?: number | string; price?: number | string } | undefined,
  options?: { isSeller?: boolean },
) {
  const homeHasPrice = hasPositiveStartPrice(data?.price);
  const [displayPrice, setDisplayPrice] = useState(() =>
    resolveInitialDisplayPrice(data),
  );
  const cardRef = useRef<HTMLAnchorElement>(null);
  const [isVisible, setIsVisible] = useState(false);

  useEffect(() => {
    if (homeHasPrice) {
      setDisplayPrice(parseStartPrice(data?.price)!);
      return;
    }
    if (options?.isSeller || data?.id == null) return;

    const cached = getCachedEventStartPrice(data.id);
    if (cached != null) {
      setDisplayPrice(cached);
    }
  }, [homeHasPrice, data?.price, data?.id, options?.isSeller]);

  useEffect(() => {
    if (options?.isSeller || homeHasPrice || !cardRef.current) return;

    const el = cardRef.current;
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) setIsVisible(true);
      },
      { rootMargin: "100px" },
    );
    observer.observe(el);
    return () => observer.disconnect();
  }, [options?.isSeller, homeHasPrice, data?.id]);

  useEffect(() => {
    if (
      options?.isSeller ||
      homeHasPrice ||
      !isVisible ||
      data?.id == null
    ) {
      return;
    }

    const eventId = String(data.id);
    const cached = getCachedEventStartPrice(eventId);
    if (cached != null) {
      setDisplayPrice(cached);
      return;
    }

    let cancelled = false;
    fetchAndCacheAvailableEventPrice(eventId).then((price) => {
      if (!cancelled) {
        setDisplayPrice(price);
      }
    });

    return () => {
      cancelled = true;
    };
  }, [isVisible, data?.id, homeHasPrice, options?.isSeller]);

  return { displayPrice, cardRef };
}
